import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Linkedin, Share2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-24 gap-4">
        <p className="font-body text-slate-400">Article non trouve</p>
        <Link to="/blog" className="text-blue-400 font-body text-sm">Retour au blog</Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="pt-24">
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${post.image_url})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 to-slate-950" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-400 font-body text-sm mb-8 transition-colors" data-testid="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </Link>
          <span className="inline-block font-body text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full mb-6">
            {post.category}
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-slate-50 mb-6 leading-[1.15]">
            {post.title}
          </h1>
          <div className="flex items-center gap-6 text-slate-400">
            {post.author_photo_url ? (
              <div className="flex items-center gap-3">
                <img src={post.author_photo_url} alt={post.author_name || post.author} className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500/30" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-body text-sm text-slate-200 font-medium">{post.author_name || post.author}</span>
                    {post.author_linkedin && (
                      <a href={post.author_linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors" data-testid="author-linkedin">
                        <Linkedin className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span className="font-body text-xs">
                      {new Date(post.published_at).toLocaleDateString("fr-FR", {
                        year: "numeric", month: "long", day: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span className="font-body text-sm">{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span className="font-body text-sm">
                    {new Date(post.published_at).toLocaleDateString("fr-FR", {
                      year: "numeric", month: "long", day: "numeric",
                    })}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl overflow-hidden border border-white/10 mb-12">
            <img src={post.image_url} alt={post.title} className="w-full h-64 md:h-96 object-cover" />
          </div>
          <div className="prose prose-invert max-w-none">
            {post.content.split("\n\n").map((paragraph, i) => (
              <p key={i} className="font-body text-base md:text-lg text-slate-300 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Share buttons */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="font-body text-xs font-medium tracking-[0.2em] uppercase text-blue-400 mb-4">Partager cet article</p>
            <div className="flex gap-3">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-linkedin"
                className="inline-flex items-center gap-2 bg-[#0A66C2] hover:bg-[#004182] text-white px-5 py-2.5 rounded-full font-body text-sm font-medium transition-all duration-300"
              >
                <Linkedin className="w-4 h-4" />
                Partager sur LinkedIn
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-twitter"
                className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:border-blue-500/30 text-slate-300 px-5 py-2.5 rounded-full font-body text-sm font-medium transition-all duration-300"
              >
                <Share2 className="w-4 h-4" />
                Partager sur X
              </a>
            </div>
          </div>

          {/* Author card */}
          {post.author_photo_url && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-4 bg-slate-900/50 border border-white/10 rounded-2xl p-6">
                <img src={post.author_photo_url} alt={post.author_name} className="w-16 h-16 rounded-full object-cover ring-2 ring-blue-500/30" />
                <div>
                  <p className="font-body text-xs text-slate-500 mb-1">Auteur</p>
                  <p className="font-body text-base font-medium text-slate-50">{post.author_name}</p>
                  {post.author_linkedin && (
                    <a
                      href={post.author_linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="author-linkedin-card"
                      className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 font-body text-sm mt-1 transition-colors"
                    >
                      <Linkedin className="w-3.5 h-3.5" />
                      Voir le profil LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
